import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { startWith, Subject, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { IFormKey, IFormSelectOption } from 'src/app/modules/shared/models/forms';
import { ICityObject, IFigureAnagrafiche, RegistryState, RegistryType } from 'src/app/modules/shared/models/policy.model';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { isv_contractor_address_form_keys, isv_contractor_residence_form_keys, isv_contractor_residence_form_validators, isv_contractor_residence_form_validators_previdential } from '../registry-form/registry-form.constants';
import { CommonUtils } from './../../../../utils/CommonUtils';
import { PREVIDENTIAL_ADDRESS_TABLE_KEYS, residence_change_previdence_form_group_config, residence_enabling_map } from './residence-change-isv.constants';

@Component({
  selector: 'app-residence-change-isv',
  templateUrl: './residence-change-isv.component.html',
  styleUrl: './residence-change-isv.component.scss'
})
export class ResidenceChangeIsvComponent implements OnInit, OnDestroy {

  constructor(
    private readonly _spinnerService: SpinnerService,
    private readonly _policyService: PolicyService,
    private readonly _alertService: AlertService
  ) { }

  @Input() countriesList: IFormSelectOption[];
  @Input() provincesList: IFormSelectOption[];
  @Input() placesList: IFormSelectOption[];
  @Input() form: FormGroup;
  @Input() additionalForm: FormGroup;
  @Input() title: string = null;
  @Input() isAddressForGianos: boolean = false;
  @Input() figureAnagrafiche: IFigureAnagrafiche[];
  @Input() registryTypeOperation: RegistryType

  formKeys: IFormKey[];
  validAddress: any[]
  additionalFormKeys: IFormKey[];
  public tableKeys = PREVIDENTIAL_ADDRESS_TABLE_KEYS;
  
  destroy$ = new Subject<void>();

  get isPolicyPrevidenziale(): boolean {
    return this._policyService.isDetailPrevidenziale
  }

  ngOnInit(): void {
    this.manageFormKeys();
    this.manageForm();
    if (this.isPolicyPrevidenziale) {
      this.patchForm();  
    }
  }

  patchForm(): void {
    this.validAddress = this.figureAnagrafiche?.filter(figure => figure.statoPolizzaPratica.trim() === RegistryState.IN_VIGORE)
    const currentAnagrafica = this.validAddress?.find(figure => figure.tipo_indirizzo === this.registryTypeOperation);
    this.form.patchValue({
      stato: currentAnagrafica.c_nazione,
      provincia: currentAnagrafica.provincia,
      cap: currentAnagrafica.cap,
      indirizzo: currentAnagrafica.indirizzo,
      localita: currentAnagrafica.localita,
      sameAsResidence: false,
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  watchForm(): void {
    this.form?.get('sameAsResidence')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedValue) => {
        if (selectedValue) {
          const rawValue = this.additionalForm.getRawValue();
          const provinceCode = this.provincesList.find(province => province.value?.toLowerCase() === rawValue?.provincia?.toLowerCase());
          const province = provinceCode ? provinceCode.key : rawValue?.provincia;
          this.retrievePlaces(province, this.formKeys, 'localita');
          this.form.patchValue({
            stato: rawValue?.stato,
            provincia: province,
            localita: rawValue?.localita,
            cap: rawValue?.cap,
            indirizzo: rawValue?.indirizzo,
            sameAsResidence: true
          }, { emitEvent: false });
        }
      });

    this.form?.get('indirizzo')?.valueChanges
      .pipe(
        startWith(this.form?.get('indirizzo')?.value),
        takeUntil(this.destroy$))
      .subscribe(() => {
        const control = this.form?.get('sameAsResidence');
        if (control) {
          control?.setValue(false, { emitEvent: false });
        }
      });

    if (this.isPolicyPrevidenziale || !this.isAddressForGianos) {
      this.setupAddressEnabling(this.form, isv_contractor_residence_form_validators_previdential);
    }
    else {
      this.setupAddressEnabling(this.form, isv_contractor_residence_form_validators);
    }
    this.setupCountryWatcher(this.form, this.formKeys);
    this.setupProvinceWatcher(this.form, this.formKeys);
  }

  setupAddressEnabling(form: FormGroup, validators: any): void {
    Object.keys(residence_enabling_map).forEach(controlName => {
      form?.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          const childControlName = residence_enabling_map[controlName];
          const control = form.get(childControlName);
          if (control) {
            if (value && form?.get(controlName)) {
              control.enable();
              control.reset();
              control.setValidators(validators[childControlName]);
              if (form?.get('stato')?.value !== 'IT' && controlName === 'localita') {
                form.get('cap').reset();
                form.get('cap').disable();
                form.get('cap').setValidators(null);
                form.get('indirizzo').enable();
              }
            } else {
              control.reset();
              control.disable();
            }
          }
        });
    });
  }

  setupCountryWatcher(form: FormGroup, formKeys: IFormKey[]): void {
    form?.get('stato')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const formKey = formKeys?.find(key => key.controlName === 'provincia');
        if (value === 'IT') {
          formKey.type = 'SELECT';
          formKey.options = this.provincesList;
        } else {
          if (this.isPolicyPrevidenziale){
            form.get('provincia').disable();
            form.get('indirizzo').enable();
          }
          formKey.type = 'INPUT';
        }
      });
  }

  setFormKeyOptions(formKeys: any[], controlName: string, options: any): void {
    const key = formKeys?.find(key => key.controlName === controlName);
    if (key) {
      key.options = options;
    }
  }

  setupProvinceWatcher(form: FormGroup, formKeys: IFormKey[]): void {
    form?.get('provincia')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const formKey = formKeys?.find(key => key.controlName === 'localita');
        if (formKey && value) {
          if (form?.get('stato')?.value === 'IT') {
            formKey.type = 'SELECT';
            if (this.isPolicyPrevidenziale) {
              this.setOptions(formKeys, 'localita', this.placesList);
            }
            this.retrievePlaces(value, formKeys, 'localita');
          } else {
            formKey.type = 'INPUT';
          }
        }
      });
  }

  manageForm(): void {
    if (this.form) {
      const provinceValue = this.form?.get('provincia')?.value;
      if (provinceValue) {
        const provinceCode = this.provincesList.find(province => province.value?.toLowerCase() === provinceValue?.toLowerCase());
        if (provinceCode) {
          this.retrievePlaces(provinceCode.key, this.formKeys, 'localita');
          this.form?.get('provincia').setValue(provinceCode.key);
        } else {
          this.retrievePlaces(provinceValue, this.formKeys, 'localita');
          this.form?.get('provincia').setValue(provinceValue);
        }
      }
    }
    if (this.additionalForm && !this.isAddressForGianos) {
      const provinceValue = this.additionalForm?.get('provincia')?.value;
      if (provinceValue) {
        const provinceCode = this.provincesList.find(province => province.value?.toLowerCase() === provinceValue?.toLowerCase());
        if (provinceCode) {
          this.retrievePlaces(provinceCode.key, this.additionalFormKeys, 'localita');
          this.additionalForm?.get('provincia').setValue(provinceCode.key);
        } else {
          this.retrievePlaces(provinceValue, this.additionalFormKeys, 'localita');
          this.additionalForm?.get('provincia').setValue(provinceValue);
        }
      }
    }
    this.watchForm();
  }

  manageFormKeys(): void {
    this.formKeys = this.isPolicyPrevidenziale
      ? CommonUtils.assign(residence_change_previdence_form_group_config)
      : this.operationId === '_VRECP' || this.isAddressForGianos
        ? CommonUtils.assign(isv_contractor_address_form_keys)
        : CommonUtils.assign(isv_contractor_residence_form_keys);

    this.additionalFormKeys = CommonUtils.assign(isv_contractor_residence_form_keys);
    this.setOptions(this.formKeys, 'stato', this.countriesList);
    if (this.form?.get('stato')?.value === 'IT') {
      this.setOptions(this.formKeys, 'provincia', this.provincesList);
    }
    this.setOptions(this.additionalFormKeys, 'stato', this.countriesList);
    if (this.additionalForm?.get('stato')?.value === 'IT') {
      this.setOptions(this.additionalFormKeys, 'provincia', this.provincesList);
    }
  }

  setOptions(keys: IFormKey[], controlName: string, options: IFormSelectOption[]) {
    if (!keys) {
      return;
    }
    const key = keys.find(k => k.controlName === controlName);
    if (key) {
      key.options = options;
    }
  }

  retrievePlaces(provinceCode: string, formKeysObj: IFormKey[], controlName: string): void {
    this._spinnerService.show();
    this._policyService.retrieveCities(provinceCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: { listaComuni: ICityObject[] }) => {
          if (response) {
            const placesList = response.listaComuni?.map((cityObj: ICityObject) => ({
              key: cityObj.cityName,
              value: cityObj.cityName
            }));
            this.setOptions(formKeysObj, controlName, placesList);
          }
          this._spinnerService.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'retirevePlaces', error);
          this._alertService.openErrorMessage('Errore nel recupero della lista delle città', 'retirevePlaces', error);
          this._spinnerService.hide();
        }
      })
    }

    get operationId(): string {
      return this._policyService.operationId;
    }

    get formTitle(): string {
      if (this.title) {
        return this.title;
      }
      return this.operationId === '_VRECP' ?
        'Dati di recapito' :
        'Dati di residenza';
    }

    get additionalFormTitle(): string {
      return this.operationId === '_VRECP' ?
        'Dati di residenza' :
        'Dati di recapito';
    }

}
